import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Check, X, ArrowRight } from "lucide-react";
import { getProducts, getCategories } from "../lib/api";
import { useQuote } from "../context/QuoteContext";
import QuoteItemDetail from "./QuoteItemDetail";

const formatEuro = (v) =>
    typeof v === "number" && v > 0
        ? `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "-";

// Normalizzazione client identica al backend
const normalize = (s) =>
    (s || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

export const ProductCatalog = ({
    selectedCategory,
    onCategoryChange,
    onAddToQuote,
    selectedIds = new Set(),
}) => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const { applyChatAction } = useQuote();

    useEffect(() => {
        setLoading(true);
        Promise.all([getProducts(), getCategories()])
            .then(([pd, cd]) => {
                setProducts(pd.products || []);
                setCategories(cd.categories || []);
            })
            .catch(() => {
                setProducts([]);
                setCategories([]);
            })
            .finally(() => setLoading(false));
    }, []);

    const activeCategory = useMemo(() => {
        if (!selectedCategory || selectedCategory === "all") return null;
        return categories.find((c) => c.id === selectedCategory) || null;
    }, [categories, selectedCategory]);

    const searchTrimmed = search.trim();

    /* Titolo dinamico in base ai filtri attivi.
       - Default → "Tutto il listino"
       - Filtro categoria attivo → nome categoria
       - Ricerca attiva → "Risultati per: 'parola'"
       - Entrambi → "Risultati per 'parola' in {categoria}"
    */
    const dynamicTitle = useMemo(() => {
        if (searchTrimmed && activeCategory) {
            return {
                eyebrow: `Risultati in ${activeCategory.name}`,
                heading: `"${searchTrimmed}"`,
            };
        }
        if (searchTrimmed) {
            return {
                eyebrow: "Risultati ricerca",
                heading: `"${searchTrimmed}"`,
            };
        }
        if (activeCategory) {
            return {
                eyebrow: "Categoria",
                heading: activeCategory.name,
            };
        }
        return {
            eyebrow: "Listino · Prezzi IVA esclusa",
            heading: "Tutto il listino",
        };
    }, [searchTrimmed, activeCategory]);

    const filtered = useMemo(() => {
        let list = products;
        if (selectedCategory && selectedCategory !== "all") {
            list = list.filter((p) => p.category === selectedCategory);
        }
        const qn = normalize(search);
        if (qn) {
            const qnNoSpace = qn.replace(/\s/g, "");
            const qWords = qn.split(" ").filter(Boolean);
            list = list.filter((p) => {
                const haystackParts = [
                    normalize(p.name),
                    normalize(p.description || ""),
                    ...(p.tags || []).map(normalize),
                    ...(p.specs || []).map(normalize),
                ];
                const haystack = haystackParts.join(" | ");
                const haystackNoSpace = haystack.replace(/\s/g, "");
                return qWords.every(
                    (w) =>
                        haystack.includes(w) ||
                        haystackNoSpace.includes(w.replace(/\s/g, ""))
                );
            });
        }
        return list;
    }, [products, selectedCategory, search]);

    return (
        <section
            id="catalogo"
            data-testid="product-catalog"
            className="relative bg-white py-14 lg:py-20"
        >
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-14">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
                    <div className="min-w-0">
                        <div
                            data-testid="catalog-eyebrow"
                            className="text-[11px] font-bold uppercase tracking-[0.3em] text-nsp-green mb-3"
                        >
                            {dynamicTitle.eyebrow}
                        </div>
                        <h2
                            data-testid="catalog-title"
                            className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-none text-nsp-green tracking-tight break-words"
                        >
                            {dynamicTitle.heading}
                        </h2>
                        {(activeCategory || searchTrimmed) && !loading && (
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <span className="text-xs text-gray-400">Filtri attivi:</span>
                                {activeCategory && (
                                    <button
                                        data-testid="active-filter-category"
                                        onClick={() => onCategoryChange && onCategoryChange("all")}
                                        className="inline-flex items-center gap-1.5 bg-nsp-green text-nsp-yellow px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full hover:bg-nsp-green-dark"
                                    >
                                        {activeCategory.name}
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                                {searchTrimmed && (
                                    <button
                                        data-testid="active-filter-search"
                                        onClick={() => setSearch("")}
                                        className="inline-flex items-center gap-1.5 bg-nsp-yellow text-nsp-green px-3 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-white border border-nsp-yellow-dark"
                                    >
                                        "{searchTrimmed}"
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                                <button
                                    data-testid="clear-all-filters"
                                    onClick={() => {
                                        setSearch("");
                                        onCategoryChange && onCategoryChange("all");
                                    }}
                                    className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-nsp-green underline"
                                >
                                    Pulisci tutti
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3 w-full lg:w-auto">
                        <div className="relative flex-1 lg:w-96">
                            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-nsp-green flex items-center justify-center pointer-events-none">
                                <Search className="w-4 h-4 text-nsp-yellow" strokeWidth={2.5} />
                            </div>
                            <input
                                data-testid="catalog-search"
                                type="text"
                                placeholder="Cerca un mezzo: miniescavatore, muletto..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                maxLength={100}
                                autoComplete="off"
                                className="w-full h-14 pl-14 pr-10 bg-white border-2 border-nsp-green/25 shadow-[0_6px_20px_rgb(10,59,37,0.10)] hover:border-nsp-green/50 focus:border-nsp-green focus:shadow-[0_6px_24px_rgb(10,59,37,0.18)] focus:outline-none text-sm font-medium placeholder:text-gray-500 rounded-full transition-all"
                            />
                            {search && (
                                <button
                                    data-testid="catalog-search-clear"
                                    onClick={() => setSearch("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-nsp-green"
                                    aria-label="Pulisci ricerca"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {!loading && filtered.length > 0 && search.trim() && (
                    <div className="mb-4 text-xs text-zinc-500" data-testid="catalog-results-count">
                        <b className="text-nsp-green">{filtered.length}</b> risultat{filtered.length === 1 ? "o" : "i"} per "<b>{search}</b>"
                    </div>
                )}

                {loading ? (
                    <div className="text-zinc-500 text-sm">Caricamento prodotti...</div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white border border-nsp-border p-12 text-center" data-testid="catalog-no-results">
                        <div className="text-nsp-green font-bold uppercase tracking-wide text-sm">
                            Nessun risultato
                        </div>
                        <div className="text-zinc-500 text-sm mt-2">
                            Prova con altri termini come "<b>escavatore</b>", "<b>muletto</b>", "<b>generatore</b>", "<b>betoniera</b>" o "<b>martello</b>".
                        </div>
                        <div className="text-zinc-400 text-xs mt-3">
                            Oppure chiamaci al 327 2237610 o chiedi al nostro assistente in chat.
                        </div>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filtered.map((p) => (
                            <ProductCard
                                key={p.id}
                                product={p}
                                onAdd={onAddToQuote}
                                onOpen={() => setDetail(p)}
                                added={selectedIds.has(p.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
            {detail && (
                <QuoteItemDetail
                    mode="catalog"
                    item={{ product: detail, days: 1, quantity: 1 }}
                    onClose={() => setDetail(null)}
                    onSave={(days, quantity) => {
                        applyChatAction({ action: "add", product: detail, days, quantity });
                        setDetail(null);
                    }}
                />
            )}
        </section>
    );
};

const ProductCard = ({ product, onAdd, onOpen, added }) => {
    return (
        <article
            data-testid={`product-card-${product.id}`}
            className="bg-white rounded-3xl border border-gray-100 flex flex-col group hover:-translate-y-1 hover:shadow-xl hover:shadow-nsp-green/10 transition-all duration-300 overflow-hidden"
        >
            <button
                type="button"
                data-testid={`product-open-${product.id}`}
                onClick={onOpen}
                className="text-left"
            >
                <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-white to-nsp-green-50 flex items-center justify-center p-6">
                    <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                </div>
            </button>
            <div className="p-5 flex flex-col gap-3 flex-1">
                <button type="button" onClick={onOpen} className="text-left">
                    <h3 className="font-bold text-nsp-green text-sm leading-tight uppercase tracking-tight hover:underline">
                        {product.name}
                    </h3>
                    <p className="text-gray-500 text-xs mt-2 line-clamp-2">
                        {product.description}
                    </p>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-nsp-yellow-dark mt-1.5 inline-flex items-center gap-1">
                        Vedi scheda <ArrowRight className="w-3 h-3" />
                    </span>
                </button>
                <div className="flex flex-wrap gap-1 mt-1">
                    {(product.specs || []).slice(0, 2).map((s, i) => (
                        <span
                            key={i}
                            className="text-[10px] uppercase tracking-wider px-2 py-1 bg-nsp-green-50 text-nsp-green rounded-full"
                        >
                            {s}
                        </span>
                    ))}
                </div>
                <div className="mt-auto pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-3 gap-1.5 text-center mb-3">
                        <PriceTier label="GIORNO" value={product.daily} />
                        <PriceTier label="SETTIMANA" value={product.weekly} />
                        <PriceTier label="MESE" value={product.monthly} />
                    </div>
                    <button
                        data-testid={`product-add-${product.id}`}
                        onClick={() => onAdd && onAdd(product)}
                        className={`w-full text-xs font-bold py-3 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-95 ${
                            added
                                ? "bg-nsp-green text-nsp-yellow"
                                : "bg-nsp-yellow text-nsp-green hover:bg-nsp-green hover:text-nsp-yellow hover:shadow-lg"
                        }`}
                    >
                        {added ? (
                            <>
                                <Check className="w-4 h-4" /> Nel preventivo
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" /> Aggiungi al preventivo
                            </>
                        )}
                    </button>
                </div>
            </div>
        </article>
    );
};

const PriceTier = ({ label, value }) => (
    <div className="bg-nsp-green-50 rounded-xl py-2 px-1">
        <div className="text-[9px] text-nsp-green/60 font-bold tracking-widest">{label}</div>
        <div className="text-nsp-green font-bold text-xs mt-0.5">{formatEuro(value)}</div>
    </div>
);

export default ProductCatalog;
